import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Label } from '../../../../../../../common/labels/labelTypes';
import CustomColorInput from '../../colorTools/CustomColorInput';
import ColorPopupContent from '../../colorTools/popup/ColorPopupContent';
import { COLOR_POPUP_MODE } from '../../colorTools/popup/colorPopupConstants';
import currentBoardSuggestedColorSelectorOnlyColors from '../../colorTools/currentBoardSuggestedColorsSelector';
import { DEFAULT_ELEMENT_COLOR, DEFAULT_LABEL_COLORS } from '../../../../../../../common/colors/colorConstants';
import { getColorDefaultHexValue } from '../../../../../../../common/colors/elementColorFormatUtil';
import { useLabelColorHandler } from '../hooks/useLabelColorHandler';

type LabelColorPickerProps = {
    label: Label;
    isActive?: boolean;
    customColorInputClassName?: string;
};

/**
 * Shared label color picker used by the label edit sheet and the label actions popover.
 */
const LabelColorPicker: React.FC<LabelColorPickerProps> = ({
    label,
    isActive = true,
    customColorInputClassName,
}: LabelColorPickerProps) => {
    const customColorInputRef = useRef<CustomColorInput>(null);
    const suggestedColors = useSelector(currentBoardSuggestedColorSelectorOnlyColors);
    const { handleTempColorChange, handleColorChange, tempColor, clearPreview } = useLabelColorHandler(label);
    const colorInputClassName = customColorInputClassName || 'absolute opacity-0 pointer-events-none';

    const selectedColor = tempColor ?? label.color;
    const labelColorHex = getColorDefaultHexValue(selectedColor) || DEFAULT_ELEMENT_COLOR;

    const openCustomColorPicker = useCallback(() => {
        customColorInputRef.current?.open();
    }, []);

    useEffect(() => {
        if (isActive) return;

        clearPreview();
    }, [clearPreview, isActive]);

    return (
        <div className="LabelColorPicker">
            <CustomColorInput
                className={colorInputClassName}
                ref={customColorInputRef}
                initialColor={labelColorHex || label.color}
                setTempColor={handleTempColorChange}
                setColor={handleColorChange}
            />

            <ColorPopupContent
                supportBackgroundColor={false}
                supportColorBar={false}
                setColor={handleColorChange}
                selectedColor={selectedColor}
                selectedColorHex={labelColorHex}
                colorPopupMode={COLOR_POPUP_MODE.COLOR}
                defaultColors={DEFAULT_LABEL_COLORS}
                suggestedColors={suggestedColors}
                openCustomColorPicker={openCustomColorPicker}
                supportDualColors={false}
            />
        </div>
    );
};

export default LabelColorPicker;
